// Store del pedido en curso. Sin frameworks: estado simple + subscribe.
//
// Forma del estado:
//   {
//     pedido:       PedidoDetalle | null,          // del backend
//     empaquetado:  { [producto_id]: cantidad },   // total acumulado en TODAS las cajas
//     cajaActual:   { lineas: LineaCaja[] },       // caja en construcción
//     lotes:        { [producto_id]: lote },       // memoria de lote DENTRO de la caja en curso
//     ssccs:        string[],                      // cajas ya cerradas en este pedido
//     paletActivo:  { sscc, numero } | null,       // palet al que se asignan las próximas cajas (Fase 10)
//   }
//
// Persistencia en sessionStorage para sobrevivir a F5 / cambio accidental de
// pestaña. La key incluye pedido_id para no mezclar pedidos.

const KEY_PREFIX = 'picking.pedido.';
let _state = _initial(null);
const _listeners = new Set();

function _initial(pedido) {
  return {
    pedido,
    empaquetado: {},
    cajaActual: { lineas: [] },
    lotes: {},
    ssccs: [],
    paletActivo: null,
    cajasPorPalet: {},   // { [paletSscc]: ssccs[] } — para contador y feedback
    packingPlan: null,   // Fase 11 — sugerencia multi-caja del backend
  };
}

function _key(pedidoId) { return KEY_PREFIX + pedidoId; }

function _persist() {
  if (!_state.pedido) return;
  try {
    sessionStorage.setItem(_key(_state.pedido.pedido_id), JSON.stringify({
      empaquetado: _state.empaquetado,
      cajaActual: _state.cajaActual,
      lotes: _state.lotes,
      ssccs: _state.ssccs,
      paletActivo: _state.paletActivo,
      cajasPorPalet: _state.cajasPorPalet,
    }));
  } catch (e) {
    console.warn('No se pudo persistir estado de pedido:', e);
  }
}

function _notify() {
  for (const l of _listeners) {
    try { l(_state); } catch (e) { console.error('pedido listener falló', e); }
  }
}

export function getState() { return _state; }

export function subscribe(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

/** Carga el pedido y rehidrata estado de sessionStorage si lo había.
 *
 *  `empaquetado` se inicializa SIEMPRE desde `linea.cantidad_enviada` del
 *  backend (verdad sobre lo ya enviado en envíos previos). A eso se le
 *  suman las cantidades de la `cajaActual` rehidratada (cosas escaneadas
 *  pendientes de "Caja lista") — eso es estado local del operario en
 *  curso. Esto garantiza que tras un envío parcial el listado siga
 *  mostrando lo ya enviado como "verde" (3/3) y solo el resto como
 *  pendiente.
 */
export function loadPedido(pedido) {
  _state = _initial(pedido);

  // Base: cantidad_enviada por línea (lo que ya está en algún envío).
  for (const l of pedido?.lineas || []) {
    if (l.cantidad_enviada && l.cantidad_enviada > 0) {
      _state.empaquetado[l.producto_id] = l.cantidad_enviada;
    }
  }

  try {
    const raw = sessionStorage.getItem(_key(pedido.pedido_id));
    if (raw) {
      const saved = JSON.parse(raw);
      _state.cajaActual = saved.cajaActual || { lineas: [] };
      _state.lotes = saved.lotes || {};
      _state.ssccs = saved.ssccs || [];
      _state.paletActivo = saved.paletActivo || null;
      _state.cajasPorPalet = saved.cajasPorPalet || {};
      // Sumar al base las cantidades de la caja en curso (no envidada aún).
      for (const l of _state.cajaActual.lineas || []) {
        _state.empaquetado[l.producto_id] = (_state.empaquetado[l.producto_id] || 0) + l.cantidad;
      }
    }
  } catch (e) { console.warn('Rehidratación falló:', e); }
  _notify();
}

/** Sustituye solo el objeto `pedido` (líneas, etc.) sin tocar caja actual,
 *  ssccs cerradas, palet activo ni lotes memorizados. Usado tras
 *  ediciones de overrides para refrescar las líneas efectivas. */
export function updatePedido(pedido) {
  _state.pedido = pedido;
  _persist();
  _notify();
}

/** Vacía completamente el estado (al cerrar pedido o cambiar de pedido). */
export function reset() {
  if (_state.pedido) {
    try { sessionStorage.removeItem(_key(_state.pedido.pedido_id)); } catch {}
  }
  _state = _initial(null);
  _notify();
}

/** Suma `cantidad` unidades del producto a la caja actual + acumulado total.
 *
 *  Trazabilidad por lote: si ya hay línea del producto **con el MISMO lote**,
 *  se suma a ella. Si el lote difiere (típico cuando el almacén termina un
 *  pallet y abre el siguiente del mismo SKU con lote distinto), se crea una
 *  línea separada para preservar el desglose. La caja puede acabar con dos
 *  líneas "Aceite × 5 lote A · Aceite × 3 lote B" — lo que el cliente
 *  espera ver en el albarán y en el shipment Dolibarr.
 */
export function addUnidades(producto, cantidad, lote) {
  const productoId = producto.producto_id;
  const loteNorm = lote || null;
  // Match exacto por producto + lote.
  const idx = _state.cajaActual.lineas.findIndex(
    l => l.producto_id === productoId && (l.lote || null) === loteNorm
  );
  if (idx >= 0) {
    _state.cajaActual.lineas[idx].cantidad += cantidad;
  } else {
    _state.cajaActual.lineas.push({
      producto_id: productoId,
      barcode: producto.barcode || null,
      descripcion: producto.descripcion,
      cantidad,
      lote: loteNorm,
      peso_unitario: producto.peso_unitario ?? null,
    });
  }
  // Memoria de lote para esta caja — el último lote queda como "actual".
  if (lote) _state.lotes[productoId] = lote;
  // Acumulado total por producto (sirve para "X / N completado", agnóstico de lote).
  _state.empaquetado[productoId] = (_state.empaquetado[productoId] || 0) + cantidad;
  _persist();
  _notify();
}

/** Cambia el lote memorizado para un producto en la caja en curso.
 *  No toca las líneas ya escaneadas; las próximas lecturas usarán el nuevo. */
export function changeLote(productoId, nuevoLote) {
  if (nuevoLote) _state.lotes[productoId] = nuevoLote;
  else delete _state.lotes[productoId];
  _persist();
  _notify();
}

/** Lee el lote memorizado actual para un producto (string o null). */
export function getLoteMemorizado(productoId) {
  return _state.lotes[productoId] || null;
}

/** Llamar tras cerrar caja con éxito: limpia caja + lotes y guarda el SSCC.
 *  Si la caja fue asignada a un palet, también lo registramos para mostrar
 *  contadores ("Palet #1 activo · 3 cajas") y mensajes en los modales. */
export function closeCaja(sscc, paletSscc = null) {
  _state.ssccs = [..._state.ssccs, sscc];
  if (paletSscc) {
    _state.cajasPorPalet = { ..._state.cajasPorPalet };
    const arr = _state.cajasPorPalet[paletSscc] || [];
    _state.cajasPorPalet[paletSscc] = [...arr, sscc];
  }
  _state.cajaActual = { lineas: [] };
  _state.lotes = {};
  _persist();
  _notify();
}

/** Cuántas cajas asignadas hay a un palet (en este pedido). */
export function cajasEnPalet(paletSscc) {
  return (_state.cajasPorPalet?.[paletSscc] || []).length;
}

/** Cuántas unidades empaquetadas hay para un producto_id (en TODAS las cajas). */
export function empaquetadasDe(productoId) {
  return _state.empaquetado[productoId] || 0;
}

/** True si todas las líneas del pedido están al 100%. */
export function pedidoCompleto() {
  if (!_state.pedido) return false;
  return _state.pedido.lineas.every(l => empaquetadasDe(l.producto_id) >= l.cantidad_pedida);
}

/** Peso total estimado de la caja actual (null si alguna línea no tiene peso_unitario). */
export function pesoCajaActual() {
  const lineas = _state.cajaActual.lineas;
  if (!lineas.length) return 0;
  if (lineas.some(l => l.peso_unitario == null)) return null;
  return lineas.reduce((sum, l) => sum + l.peso_unitario * l.cantidad, 0);
}

// --------------------------- Packing plan (Fase 11) ---------------------------

/** Setea el plan de packing actual (lo trae el backend). No persiste en
 *  sessionStorage — el plan se recalcula barato y reflejar siempre el
 *  estado del backend evita inconsistencias entre pestañas. */
export function setPackingPlan(plan) {
  _state.packingPlan = plan || null;
  _notify();
}

// --------------------------- Palet activo (Fase 10) ---------------------------

/** Marca el palet recién creado como "activo". Las próximas cajas que cierre
 *  el operario llevarán este palet_sscc en el POST a /cajas/crear. */
export function setPaletActivo(palet) {
  _state.paletActivo = palet ? { sscc: palet.sscc, numero: palet.numero_palet } : null;
  _persist();
  _notify();
}

/** Deja de paletizar: las próximas cajas van sueltas. */
export function clearPaletActivo() {
  _state.paletActivo = null;
  _persist();
  _notify();
}
